import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import { useBoolean } from "../../../../hooks/useBoolean";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import Iconify from "../../../../components/iconify";
import { authenticationLogin } from "../../authenticationSlice";
import { RHFTextField } from "src/components/hook-form";
import FormProvider from "src/components/hook-form/form-provider";
import { useTranslate } from "src/locales";
import Turnstile from "react-turnstile";
import { setSelectedOfficeId } from "../../authenticationSlice";
import authService from "../../../../api/auth/authService";
import storage from "../../../../utils/storage/cryptoStorage";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";

function Login() {
  const [errorMsg, setErrorMsg] = useState("");
  const { t } = useTranslate();
  const password = useBoolean();
  const dispatch = useAppDispatch();
  const [turnstileToken, setTurnstileToken] = useState("");
  const [officeList, setOfficeList] = useState([]);
  const [selectedOfficeId, setSelectedOfficeIdLocal] = useState<number | "">(
    ""
  );

  const { error, validationErrors } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const fetchOfficeList = async () => {
      try {
        const response = await authService.getOfficeList();
        if (response.issuccess) {
          setOfficeList(response.data);

          const lastSelectedOffice = storage.getLocalStorage(
            "lastSelectedOfficeId"
          );
          if (lastSelectedOffice) {
            const officeId = Number(lastSelectedOffice);
            const officeExists = response.data.some(
              (office: any) => office.id === officeId
            );
            if (officeExists) {
              setSelectedOfficeIdLocal(officeId);
              dispatch(setSelectedOfficeId(officeId));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching office list:", error);
      }
    };
    fetchOfficeList();
  }, [dispatch]);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required(t("login.username-cannot-be-empty")),
    password: Yup.string().required(t("login.password-cannot-be-empty")),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    setErrorMsg("");
    if (process.env.REACT_APP_RECAPTCHA === "ON") {
      if (!turnstileToken) {
        setErrorMsg("Lütfen CAPTHCHA doğrulamasını tamamlayın");
        return;
      }
    }

    const payload = {
      ...data,
      officeid: typeof selectedOfficeId === "number" ? selectedOfficeId : 0,
      turnstileToken,
    };
    dispatch(authenticationLogin(payload));
  });

  // Clear local error message when user edits fields
  const watchFields = watch();
  useEffect(() => {
    if (errorMsg) setErrorMsg("");
  }, [watchFields.username, watchFields.password, errorMsg, turnstileToken]);

  const renderForm = (
    <Stack spacing={2.5}>
      <RHFTextField
        name="username"
        label={t("login.username")}
        error={!!validationErrors?.Username}
        helperText={validationErrors?.Username?.join(", ")}
      />

      <RHFTextField
        name="password"
        label={t("login.password")}
        type={password.value ? "text" : "password"}
        error={!!validationErrors?.Password}
        helperText={validationErrors?.Password?.join(", ")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify
                  icon={
                    password.value ? "solar:eye-bold" : "solar:eye-closed-bold"
                  }
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <FormControl fullWidth>
        <InputLabel shrink>{t("office-list")}</InputLabel>
        <Select
          label={t("office-list")}
          value={selectedOfficeId}
          onChange={(event) => {
            const selectedId = Number(event.target.value);
            setSelectedOfficeIdLocal(selectedId);
            dispatch(setSelectedOfficeId(selectedId));
            storage.saveLocalStorage(
              "lastSelectedOfficeId",
              selectedId.toString()
            );
          }}
        >
          {officeList.map((item: any) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Link
        variant="body2"
        color="inherit"
        underline="always"
        sx={{ alignSelf: "flex-end" }}
      >
        {t("login.forgot-password")}
      </Link>

      {process.env.REACT_APP_RECAPTCHA === "ON" && (
        <Turnstile
          sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY!}
          onVerify={(turnstileToken) => setTurnstileToken(turnstileToken)}
          theme="light"
          refreshExpired="auto"
        />
      )}

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {t("login.login")}
      </LoadingButton>

      <ByzMuiTypography>{t("version")} : {process.env.REACT_APP_VERSION} | {t("update-date")} : {process.env.REACT_APP_VERSION_UPDATE_DATE}</ByzMuiTypography>
    </Stack>
  );

  return (
    <>
      {!!error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <FormProvider methods={methods} onSubmit={onSubmit}>
        {renderForm}
      </FormProvider>
    </>
  );
}

export default Login;
